import { Alert, Box, LinearProgress, Link, Typography } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import Button from '../../components/button/Button';
import Card from '../../components/card/Card';
import Modal from '../../components/modal/Modal';
import Page from '../../components/page/Page';
import AllRates from './components/allRates/AllRates';
import CompareChart from './components/compareChart/CompareChart';
import SolarGeneration from './components/solarGeneration/SolarGeneration';
import Filters from './components/filters/Filters';
import RateInfo from './components/rateInfo/RateInfo';
import Wizard from './components/wizard/Wizard';
import { useHistoricalLoadProfile } from './hooks/useHistoricalLoadProfile';
import HistoricalDataModal from './components/HistoricalDataModal';
import { useUserPrefs } from '../../context/UserPrefsProvider';
import GaTracker from '../../utils/GaTracker/GaTracker';
import { DEFAULT_SHOW_HISTORICAL_OPTIONS } from '../../utils/assumptions';

// Known error codes are published here:
// https://docs.google.com/spreadsheets/d/1zF2AhU6VpzRMal9WmbYyeLDzCOQgZm2yknVcHWBQRBI/edit?gid=0#gid=0
const ERROR_CODE_TRANSLATION_IDS = [
  'rates.historicalDataError.NO_USAGE_POINTS_FOUND',
  'rates.historicalDataError.BAD_USAGE_POINT_FEED_FORMAT',
  'rates.historicalDataError.MULTIPLE_USAGE_POINTS_FOUND',
  'rates.historicalDataError.NOT_ENOUGH_DATA',
  'rates.historicalDataError.UNKNOWN_ERROR',
];

export default function Rates({
  testLoadProfile,
  showWizardByDefault = true,
  showHistoricalOptions: configuredShowHistoricalOptions = DEFAULT_SHOW_HISTORICAL_OPTIONS,
}: {
  testLoadProfile?: number[];
  showWizardByDefault?: boolean;
  showHistoricalOptions?: boolean;
}) {
  const { formatMessage } = useIntl();
  const { userPrefs } = useUserPrefs();

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');
  const openHistorical = searchParams.get('open-historical');
  const shouldForceSmd = searchParams.get('force-smd') === 'true';

  const showHistoricalOptions = configuredShowHistoricalOptions || shouldForceSmd;

  const [showWizard, setShowWizard] = useState(
    Boolean(showWizardByDefault && !token && !openHistorical),
  );
  const [showFilters, setShowFilters] = useState(false);
  const [historicalDataOpen, setHistoricalDataOpen] = useState(Boolean(openHistorical));

  const { loadProfile, usingHistorical, progress, reset, error, errorCode } =
    useHistoricalLoadProfile();

  const isHistoricalFailed = error;
  const isHistoricalInProgress = usingHistorical && !loadProfile && !isHistoricalFailed;
  const errorTranslationId = errorCode && `rates.historicalDataError.${errorCode}`;

  if (process.env.REACT_APP_ENVIRONMENT !== 'production' && usingHistorical && !!loadProfile) {
    (window as any).loadProfile = loadProfile;
  }

  return (
    <Page>
      <Box p={{ xs: 2, sm: 4 }} bgcolor="grey.100">
        <Wizard open={showWizard} onClose={() => setShowWizard(false)} />
        <HistoricalDataModal
          open={historicalDataOpen}
          onClose={() => setHistoricalDataOpen(false)}
        />
        <Box display="flex" flexDirection="column" gap="1rem" maxWidth="1140px" m="auto">
          <Box display="flex" gap="1.5rem" justifyContent="space-between">
            <Box>
              <Typography variant="h1" component="h1">
                {formatMessage({ id: 'rates.header' })}
              </Typography>
              <Typography
                variant="body1"
                component={'h2'}
                fontSize="1.25rem"
                fontWeight="300"
                mb="0.5rem"
              >
                {formatMessage({ id: 'rates.description' })}
              </Typography>
            </Box>
          </Box>
          <Box display={{ xs: 'block', md: 'none' }} width="100%">
            <Button fullWidth onClick={() => setShowFilters(true)} action="rates-cta-filter">
              {formatMessage({ id: 'rates.filtersCta' })}
            </Button>
          </Box>
          <Box
            display="flex"
            gap="1.5rem"
            justifyContent="space-around"
            alignItems="flex-start"
            flexWrap="wrap"
          >
            <Box display={{ xs: 'none', md: 'block' }} flex="1 1" minWidth="250px">
              <Filters
                usingHistorical={usingHistorical}
                resetHistorical={reset}
                setHistoricalDataOpen={setHistoricalDataOpen}
                showHistoricalOptions={showHistoricalOptions}
              />
            </Box>
            <Box flex="3 1" minWidth="300px" display="flex" flexDirection="column" gap="1rem">
              {isHistoricalInProgress && (
                <Card>
                  <Typography>{formatMessage({ id: 'rates.historicalLoading' })}</Typography>
                  <LinearProgress variant="determinate" value={progress * 100} />
                </Card>
              )}
              {isHistoricalFailed && (
                <Card>
                  <Alert
                    onClose={reset}
                    severity="info"
                    variant="filled"
                    sx={{ width: '100%', mb: 2 }}
                  >
                    {formatMessage({ id: 'rates.historicalDataError.alert' })}
                  </Alert>
                  {errorTranslationId &&
                    ERROR_CODE_TRANSLATION_IDS.includes(errorTranslationId) && (
                      <Typography>{formatMessage({ id: errorTranslationId })}</Typography>
                    )}
                  <Typography>
                    {formatMessage({ id: 'rates.historicalDataError.useEstimates' })}{' '}
                    <Link
                      component="button"
                      onClick={() => {
                        GaTracker.trackEvent({
                          category: 'Button',
                          action: 'rates-show-historical-data',
                          label: 'Rates',
                        });
                        setHistoricalDataOpen(true);
                      }}
                      underline="none"
                      color="blue.700"
                      sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                    >
                      {formatMessage({ id: 'rates.historicalDataError.tryAgain' })}
                    </Link>
                  </Typography>
                </Card>
              )}

              <Card sx={{ padding: 0 }}>
                <CompareChart
                  historicalLoadProfile={testLoadProfile || loadProfile}
                  usingHistorical={usingHistorical}
                  onShowWizard={() => setShowWizard(true)}
                />
              </Card>
              {userPrefs.hasSolarAtHome && (
                <Card>
                  <SolarGeneration />
                </Card>
              )}
              <Card>
                <AllRates
                  historicalLoadProfile={testLoadProfile || loadProfile}
                  usingHistorical={usingHistorical}
                />
              </Card>
              <Card>
                <RateInfo />
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      <Modal
        open={showFilters}
        onClose={() => setShowFilters(false)}
        title={formatMessage({ id: 'rates.filtersCta' })}
      >
        <Box p={{ xs: 1, sm: 2 }}>
          <Filters
            usingHistorical={usingHistorical}
            resetHistorical={reset}
            setHistoricalDataOpen={setHistoricalDataOpen}
            showHistoricalOptions={showHistoricalOptions}
          />
        </Box>
      </Modal>
    </Page>
  );
}
